import { defineComponent as _defineComponent } from 'vue'
import { Events, Invoices, ScheduleRequest } from "@busy-human/k9-library";
import type { DataInstance } from "@busy-human/gearbox";
import { ref, watch, computed, onMounted, onUpdated } from "vue";
import { useScheduleRequests } from "@/store/useScheduleRequests";
import { useUserspace } from "@/store/useUsers";
import { useDogspace } from "@/store/dogspaces";
import { useCalendars } from "@/store/useCalendars";
import {MGMT_CONSTS} from "@/util/constants";
import { useTrainers } from "@/store/useTrainers";
import { useToast } from "vue-toastification";
import { storage } from '@/util/firebase';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';

/* SVGs */
import DiscountIcon from "@/resources/icons/discount.svg";
import CloseIcon from "@/resources/icons/close.svg";
import PersonIcon from "@/resources/icons/person.svg";
import ContractIcon from "@/resources/icons/contract.svg";
import CalendarIcon from "@/resources/icons/calendar_month.svg";
import PetIcon from "@/resources/icons/pets.svg";
import PriceIcon from "@/resources/icons/price.svg";
import { useAgreements } from "@/store/useAgreements";
import { getAgreementUrl } from "@/util/functions";
import { Agreement } from "@busy-human/contract-module";

const msPerMinute = 60000;

type Props = {
	id: string
}
type Emits = {
	(e: "close"): void
}
const EQUIP_PRICE = 350;

export default /*@__PURE__*/_defineComponent({
  __name: 'update-request',
  props: {
    id: {}
  },
  emits: ["close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const toast = useToast();

const props = __props;
const emit = __emit;
const requestStore = useScheduleRequests();
const userStore = useUserspace();
const agreements = useAgreements();
const dogStore = useDogspace();
const calendarStore = useCalendars();
const trainerStore = useTrainers();

const today             = ref(new Date().toISOString().split('T')[0]);
const workingRequest    = ref<DataInstance<ScheduleRequest.Model>>({... requestStore.requests.get(props.id)!});
const requestContract   = ref<DataInstance<Agreement.Model<any, any>> | null>(agreements.getAgreementFromRequestId(props.id) || null);
const inputDate         = ref<string>("");
const boardingStartTime = ref<string>("");
const boardingEndDate   = ref<string>("");
const boardingEndTime   = ref<string>("");
const boardingStartOkay = ref<boolean>(false);
const boardingEndOkay   = ref<boolean>(false);
const discountPercent   = ref<number>(0);
const selectedTrainer   = ref<string | null>(null);
const eventLength       = ref<number | null>(null);
const loadingText		= ref<string | null>(null);

// const boardingPrice = Invoices.calculatePriceOfBoarding(workingRequest.value)
// const trainingPrice  = Invoices.EventPricing[req.trainingType]?.[weeks];
const price = ref<number | null>(null);

watch(() => props.id, id => {
	workingRequest.value = {... requestStore.requests.get(id)!};

	// Generate a timestamp with a local timezone offset
	let d = new Date(workingRequest.value.startTime);
	d = new Date(d.getTime() - (d.getTimezoneOffset() * msPerMinute));
	let ts = d.toISOString().slice(0, -1);
	let tmp = ts.indexOf('T');
	inputDate.value = ts.substring(0, tmp);

	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		boardingStartTime.value = ts.substring(tmp + 1);

		d = new Date(workingRequest.value.endTime);
		d = new Date(d.getTime() - (d.getTimezoneOffset() * msPerMinute));
		ts = d.toISOString().slice(0, -1);

		tmp = ts.indexOf('T');
		boardingEndDate.value = ts.substring(0, tmp);
		boardingEndTime.value = ts.substring(tmp + 1);
	}else if(id){
		agreements.waitForReady().then(() => {
			requestContract.value = agreements.getAgreementFromRequestId(props.id) || null;
		});
		
	}
}, {immediate: true});

watch(inputDate, d => {
	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		const tmp = new Date(`${d}T${boardingStartTime.value}`);
		workingRequest.value.startTime = tmp.toISOString();
	} else {
		const tmp = new Date(d);
		workingRequest.value.startTime = new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate()+1).toISOString();
	}
});

watch(boardingEndDate, d => {
	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		const tmp = new Date(`${d}T${boardingEndTime.value}`);
		workingRequest.value.endTime = new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate(), tmp.getHours(), tmp.getMinutes(), tmp.getSeconds()).toISOString();
	}
});

watch(boardingStartTime, t => {
	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		const tmp = new Date(`${inputDate.value}T${t}`);
		workingRequest.value.startTime = tmp.toISOString();
	}
});

watch(boardingEndTime, t => {
	if(workingRequest.value.eventType === Events.EventType.Boarding) {
		const tmp = new Date(`${boardingEndDate.value}T${t}`);
		workingRequest.value.endTime = tmp.toISOString();
	}
});

const user = computed(() => userStore.users[workingRequest.value.userId]);
const dog = computed(() => dogStore.dogs[workingRequest.value.dogId]);

const deducedName = computed(() => (user.value?.name?.fullName)?.trim());

const isValid = computed(() => {
	if(!deducedName.value) return false;
	if(workingRequest.value.eventType === Events.EventType.Training) {
		return !!eventLength.value && !!selectedTrainer.value;
	} else if(workingRequest.value.eventType === Events.EventType.Boarding) {
		return (!workingRequest.value.companyPickup || boardingStartOkay.value) &&
			(!workingRequest.value.companyDropoff || boardingEndOkay.value);
	} else {
		return true;
	}
});

const useEquipPrice = ref(false);

const getPrice = () => {
	if(workingRequest.value.eventType === Events.EventType.Training) {
		const tmpPrice = Invoices.EventPricing[workingRequest.value.trainingType]?.[eventLength.value ?? 0] ?? null;
		if((tmpPrice || tmpPrice === 0) && useEquipPrice.value ){
			price.value = tmpPrice + EQUIP_PRICE;
		}else{
			price.value = tmpPrice;
		}
	} else if(workingRequest.value.eventType === Events.EventType.Boarding) {
		price.value = Invoices.calculatePriceOfBoarding(workingRequest.value);
	}
};

onUpdated(() => {
	getPrice();
});

onMounted(() => {
	getPrice();
	console.log("REQUEST INFO:", workingRequest.value);
	// document.getElementsByName("dateSelector")[0].setAttribute('min', today);
});

const availableWeeks = computed<number[]>(() => {
	if(workingRequest.value.eventType !== Events.EventType.Training){
		return [];
	} else {
		return MGMT_CONSTS.EVENT_LENGTH[workingRequest.value.trainingType];
	}
});

const loading = ref(false);
const error = ref<null | string>(null);
async function declineRequest() {
	loading.value = true;
	await requestStore.declineRequest(props.id);
	loading.value = false;
	emit('close');
}
const useCustomPrice = ref(false);
const customPrice = ref(1);
const estimatedPrice = computed(() => {
	if(!eventLength.value) return 'Enter Price';
	if(workingRequest.value.eventType === Events.EventType.Training){
		return Invoices.EventPricing[workingRequest.value.trainingType]?.[eventLength.value];
	}else if(workingRequest.value.eventType === Events.EventType.Boarding){
		return Invoices.calculatePriceOfBoarding(workingRequest.value) || 'N/A';
	}else{
		return 'N/A';
	}
});

onMounted(() => {
	console.log("REQUEST INFO: ", workingRequest.value);
});

// eslint-disable-next-line complexity
async function acceptRequest() {
	if(!dog.value) return;
	loading.value = true;

	console.log("Accepting...");
	error.value = null;

	try {
		// TODO: This could be optimized a little bit
		if(workingRequest.value.eventType === Events.EventType.Training) {
			if(!eventLength.value) throw new Error("Event length not set");
			if(useCustomPrice.value || (useEquipPrice.value && price.value !== null)){
				await requestStore.acceptRequest({
					reqId: props.id, 
					req: workingRequest.value, 
					dog: dog.value, 
					trainerId: selectedTrainer.value || undefined, 
					discountPercent: discountPercent.value,
					weeks: eventLength.value, 
					customPrice: useCustomPrice.value ? customPrice.value : price.value!,
					owner: user.value
				});
			}else{
				await requestStore.acceptRequest({
					reqId: props.id, 
					req: workingRequest.value, 
					dog: dog.value, 
					trainerId: selectedTrainer.value || undefined, 
					discountPercent: discountPercent.value,
					weeks: eventLength.value,
					owner: user.value
				});
			}
		} else if(workingRequest.value.eventType === Events.EventType.Boarding) {
			if(useCustomPrice.value || (useEquipPrice.value && price.value !== null)){
				await requestStore.acceptRequest({
					reqId: props.id, 
					req: workingRequest.value, 
					dog: dog.value, 
					discountPercent: discountPercent.value,
					customPrice: useCustomPrice.value ? customPrice.value : price.value!,
					owner: user.value
				});
			}else{
				await requestStore.acceptRequest({
					reqId: props.id, 
					req: workingRequest.value, 
					discountPercent: discountPercent.value,
					dog: dog.value, 
					owner: user.value
				});
			}
		}
		toast(`Successfully accepted request for ${dog.value.name} on ${new Date(workingRequest.value.startTime).toLocaleDateString()}`);
		console.log("Done!...");
		console.log("ACCEPTED:", workingRequest.value);
		emit('close');
	} catch (e) {
		console.error(e);
		toast.error(`Error while accepting request for ${dog.value.name}.`);
		error.value = `Error while accepting: ${(e as Error).message}`;
	} finally {
		loading.value = false;
	}
}

const openDocusignEnvelope = async (agreementId: string, status: string) => {
	if (status !== "completed") {
		error.value = "Cannot open incomplete contract";
		return;
	}
	loading.value = false;
	// console.log("HERE:", agreementId)
	error.value = null;
	const agreement = agreements.requests.get(agreementId);
	if (agreement) {
		console.log("Agreement", agreement);
		
		try {
			let agreementUrl = '';
			if(agreement?.pdfUrl){
				const fileRef = storageRef(storage, agreement?.pdfUrl);

				agreementUrl = await getDownloadURL(fileRef);
			}else{
				const data = await getAgreementUrl({ agreementId: agreement.$id, isSender: true });

				agreementUrl = data.data.url;
			}

			if(!agreementUrl) throw new Error('Unable to access contract');
			loading.value = false;
			window.open(agreementUrl, '_blank');
			
		} catch (e) {
			error.value = "Unable to access Contract";
			loading.value = false;
		}

	}
	// console.log("HERE 2:", loading.value)
	// loading.value = false;
};

const __returned__ = { toast, msPerMinute, props, emit, requestStore, userStore, agreements, dogStore, calendarStore, trainerStore, today, workingRequest, requestContract, inputDate, boardingStartTime, boardingEndDate, boardingEndTime, boardingStartOkay, boardingEndOkay, discountPercent, selectedTrainer, eventLength, loadingText, price, user, dog, deducedName, isValid, EQUIP_PRICE, useEquipPrice, getPrice, availableWeeks, loading, error, declineRequest, useCustomPrice, customPrice, estimatedPrice, acceptRequest, openDocusignEnvelope, get Events() { return Events }, get Invoices() { return Invoices }, get ScheduleRequest() { return ScheduleRequest }, ref, watch, computed, onMounted, onUpdated, get useScheduleRequests() { return useScheduleRequests }, get useUserspace() { return useUserspace }, get useDogspace() { return useDogspace }, get useCalendars() { return useCalendars }, get MGMT_CONSTS() { return MGMT_CONSTS }, get useTrainers() { return useTrainers }, get useToast() { return useToast }, get storage() { return storage }, get storageRef() { return storageRef }, get getDownloadURL() { return getDownloadURL }, get DiscountIcon() { return DiscountIcon }, get CloseIcon() { return CloseIcon }, get PersonIcon() { return PersonIcon }, get ContractIcon() { return ContractIcon }, get CalendarIcon() { return CalendarIcon }, get PetIcon() { return PetIcon }, get PriceIcon() { return PriceIcon }, get useAgreements() { return useAgreements }, get getAgreementUrl() { return getAgreementUrl }, get Agreement() { return Agreement } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})